import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Database } from "lucide-react";
import EditEntryModal from "../components/EditEntryModal";
import SectionCard from "../components/ui/cards/SectionCard";
import EnterpriseDataTable from "../components/ui/tables/EnterpriseDataTable";
import PageHeader from "../components/layout/PageHeader";
import { useAuthContext } from "../context/AuthContext";
import { useMasters } from "../context/MastersContext";
import { useEnhancedExpenses } from "../hooks/useEnhancedExpenses";
import { Expense } from "../types/expenseTypes";
import { updateApprovalStatus } from "../utils/approval/updateApprovalStatus";
import { moveDocToArchive } from "../utils/MasterService";
import Button from '../ui/Button';
import { hasAnyRole, UserRole } from "../utils/rbac";
import { CostRollupService } from "../services/costRollupService";
import ConfirmDialog from "../components/ui/modals/ConfirmDialog";
import { useConfirmDialog } from "../hooks/useConfirmDialog";

/**
 * ExpenseManagementPage - Unified operational data management page
 * 
 * This page consolidates the best features from:
 * - ReportsPage (/reports) - Cost per Bag KPI, Advanced export, RBAC
 * - EnterpriseTablePage (/enterprise-table) - Nature filter, Enhanced edit modal, Better UI
 * 
 * Purpose: Operational tool for viewing, editing, approving, and managing expense records
 * Users: Editor, Approver, MIS Viewer, Admin, Super Admin
 * 
 * Key Features:
 * - Advanced filtering including Nature filter (routine, stock, payroll, capital, admin/finance)
 * - Enhanced edit modal with better UX
 * - Cost per Bag KPI integration in exports
 * - Role-based permissions (RBAC)
 * - Approve/Reject workflow
 * - Delete/Archive (admin only)
 * - Field selection for exports
 * - Updated At/By audit columns
 */
const ExpenseManagementPage: React.FC = () => {
  const { roles } = useAuthContext();
  
  // Check if user has access to this page
  const hasAccess = hasAnyRole(roles as UserRole[], ['editor', 'manager', 'admin', 'superadmin']);

  if (!hasAccess) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="text-center py-8">
          <Database className="h-16 w-16 text-text-placeholder mx-auto mb-4" />
          <div className="text-ds-status-error-main mb-4">Access Denied</div>
          <p className="text-text-secondary">You don't have permission to access Expense Management.</p>
          <p className="text-sm text-text-muted mt-2">This page is for Editors, Approvers, and Administrators only.</p>
          <p className="text-sm text-text-muted">Contact your administrator for access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 pb-32">
      <PageHeader 
        icon={<Database size={24} />}
        title="Expense Management"
        subtitle="View, edit, approve, and manage expense records. Use filters to find specific expenses and take actions based on your role."
      />

      {/* Main Content */}
      <div className="w-full max-w-full mx-auto space-y-4">
        <ExpenseManagementTable />
      </div>
    </div>
  );
};

// Main table component
const ExpenseManagementTable: React.FC = () => {
  const { roles, assignedUnits, user } = useAuthContext();
  const { types, units, costCenters, categories, shops, vendors } = useMasters();
  const isAdmin = roles.includes("admin") || roles.includes("superadmin");
  const { confirm, dialogProps } = useConfirmDialog();

  // Helper function to get shop name from ID
  const getShopName = (shopId: string): string => {
    if (!shopId) return 'N/A';
    const shop = shops.find(s => s.id === shopId);
    return shop?.name || shopId; // Fallback to ID if shop not found
  };

  // Helper function to get vendor name from ID
  const getVendorName = (vendorId: string): string => {
    if (!vendorId) return 'N/A';
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.name || vendorId; // Fallback to ID if vendor not found
  };

  // Use the enhanced expenses hook for user data - moderate load for proper pagination
  const {
    expenses,
    loading,
    error,
    hasNextPage,
    totalCount,
    loadNextPage,
    refresh,
    filterData
  } = useEnhancedExpenses({
    pageSize: 500, // Proven to work well with good performance
    enableRealtime: false
  });

  // State for full filter options data (only loaded for admins)
  const [fullFilterData, setFullFilterData] = useState<Expense[]>([]);
  const [filterDataLoading, setFilterDataLoading] = useState(false);

  // Load full data for filter options (admin only)
  useEffect(() => {
    if (isAdmin && fullFilterData.length === 0 && !filterDataLoading) {
      const loadFullFilterData = async () => {
        setFilterDataLoading(true);
        try {
          // Import Firebase functions
          const { collection, query, orderBy, limit, getDocs } = await import('firebase/firestore');
          const { db } = await import('../firebase');

          // Get all expenses for filter options (no RBAC filtering for admins)
          const q = query(
            collection(db, 'expenses'),
            orderBy('createdAt', 'desc'),
            limit(2000) // Increased for more comprehensive filter options
          );

          const snapshot = await getDocs(q);
          const allExpenses = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate?.() || new Date(),
            date: doc.data().date?.toDate?.() || new Date(),
          })) as Expense[];

          setFullFilterData(allExpenses);
        } catch (error) {
          console.error('Error loading full filter data:', error);
        } finally {
          setFilterDataLoading(false);
        }
      };

      loadFullFilterData();
    }
  }, [isAdmin, fullFilterData.length, filterDataLoading]);

  // Use full data for filter options if available, otherwise use user data
  const filterOptionsData = isAdmin && fullFilterData.length > 0 ? fullFilterData : expenses;

  const [rowLoading, setRowLoading] = useState<Record<string, boolean>>({});
  const [editExpense, setEditExpense] = useState<Expense | null>(null);

  // Role-based permissions
  const EDIT_PERMISSIONS = {
    user: 365,
    editor: 365,
    approver: 365,
    admin: 365,
    'super-admin': 365,
    'superadmin': 365,
    'misViewer': 0, // MIS Viewer cannot edit
  } as const;

  const canUserEdit = (expense: Expense): boolean => {
    // Admins can edit anything immediately (no time check)
    const adminCheck = isAdmin || roles.includes('admin') || roles.includes('superadmin');

    if (adminCheck) {
      return true;
    }
    
    if (!expense.createdAt) return false;
    if (!assignedUnits.includes(expense.unitId)) return false;
    if (roles.includes('misViewer')) return false;

    const maxDays = Math.max(
      ...roles.map(role => EDIT_PERMISSIONS[role as keyof typeof EDIT_PERMISSIONS] || 0)
    );

    const createdDate = new Date(expense.createdAt);
    const daysDiff = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

    return daysDiff <= maxDays;
  };

  const handleEdit = (expense: Expense) => {
    if (!canUserEdit(expense)) {
      toast.error("You don't have permission to edit this expense");
      return;
    }
    setEditExpense(expense);
  };

  const handleEditClose = () => {
    setEditExpense(null);
  };

  const handleEditSaved = () => {
    setEditExpense(null);
    refresh();
    toast.success("Expense updated successfully");
  };

  const handleDelete = async (expense: Expense) => {

    if (!roles.includes('admin') && !roles.includes('superadmin')) {
      toast.error("You don't have permission to delete expenses");
      return;
    }

    const confirmed = await confirm({
      title: "Archive Expense",
      message: "Archive this entry? This action will move the expense to archive.",
      confirmText: "Archive",
      variant: "warning",
    });

    if (!confirmed) return;

    setRowLoading(prev => ({ ...prev, [expense.id]: true }));
    try {
      await moveDocToArchive(expense.id);
      toast.success('Entry archived successfully');
      refresh();
    } catch (err: any) {
      console.error('Error archiving expense:', err);
      toast.error(err.message || 'Failed to archive expense');
    } finally {
      setRowLoading(prev => ({ ...prev, [expense.id]: false }));
    }
  };

  const handleApprove = async (expense: Expense) => {
    if (!roles.includes("admin") && !roles.includes("manager") && !roles.includes("superadmin")) {
      toast.error("You don't have permission to approve expenses");
      return;
    }

    setRowLoading(prev => ({ ...prev, [expense.id]: true }));

    try {
      const result = await updateApprovalStatus(expense.id, 'approved', user?.email || '');

      if (result.success) {
        toast.success('Expense approved successfully');
        refresh();
      } else {
        toast.error(result.error || 'Failed to approve expense');
      }
    } catch (error) {
      console.error('Error approving expense:', error);
      toast.error('An error occurred while approving the expense');
    } finally {
      setRowLoading(prev => ({ ...prev, [expense.id]: false }));
    }
  };

  const handleReject = async (expense: Expense) => {
    if (!roles.includes("admin") && !roles.includes("manager") && !roles.includes("superadmin")) {
      toast.error("You don't have permission to reject expenses");
      return;
    }

    setRowLoading(prev => ({ ...prev, [expense.id]: true }));

    try {
      const result = await updateApprovalStatus(expense.id, 'rejected', user?.email || '');

      if (result.success) {
        toast.success('Expense rejected successfully');
        refresh();
      } else {
        toast.error(result.error || 'Failed to reject expense');
      }
    } catch (error) {
      console.error('Error rejecting expense:', error);
      toast.error('An error occurred while rejecting the expense');
    } finally {
      setRowLoading(prev => ({ ...prev, [expense.id]: false }));
    }
  };

  // Bulk approval handler - processes multiple expenses at once with single toast
  const handleBulkApprove = async (expenses: Expense[]) => {
    if (!roles.includes("admin") && !roles.includes("approver") && !roles.includes("superadmin")) {
      toast.error("You don't have permission to approve expenses");
      return;
    }

    if (expenses.length === 0) {
      toast.warning("No expenses selected for approval");
      return;
    }

    const toastId = toast.info(`Processing ${expenses.length} expenses...`, { autoClose: false });

    try {
      const results = await Promise.allSettled(
        expenses.map(expense => 
          updateApprovalStatus(expense.id, 'approved', user?.email || '')
        )
      );

      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failed = results.length - successful;

      toast.dismiss(toastId);

      if (failed === 0) {
        toast.success(`✅ Successfully approved ${successful} expense${successful !== 1 ? 's' : ''}`);
      } else if (successful === 0) {
        toast.error(`❌ Failed to approve all ${failed} expense${failed !== 1 ? 's' : ''}`);
      } else {
        toast.warning(`⚠️ Approved ${successful}, Failed ${failed} out of ${results.length} expenses`);
      }

      refresh();
    } catch (error) {
      console.error('Bulk approve error:', error);
      toast.dismiss(toastId);
      toast.error('An error occurred during bulk approval');
    }
  };

  // Bulk rejection handler - processes multiple expenses at once with single toast
  const handleBulkReject = async (expenses: Expense[]) => {
    if (!roles.includes("admin") && !roles.includes("approver") && !roles.includes("superadmin")) {
      toast.error("You don't have permission to reject expenses");
      return;
    }

    if (expenses.length === 0) {
      toast.warning("No expenses selected for rejection");
      return;
    }

    const toastId = toast.info(`Processing ${expenses.length} expenses...`, { autoClose: false });

    try {
      const results = await Promise.allSettled(
        expenses.map(expense => 
          updateApprovalStatus(expense.id, 'rejected', user?.email || '')
        )
      );

      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failed = results.length - successful;

      toast.dismiss(toastId);

      if (failed === 0) {
        toast.success(`✅ Successfully rejected ${successful} expense${successful !== 1 ? 's' : ''}`);
      } else if (successful === 0) {
        toast.error(`❌ Failed to reject all ${failed} expense${failed !== 1 ? 's' : ''}`);
      } else {
        toast.warning(`⚠️ Rejected ${successful}, Failed ${failed} out of ${results.length} expenses`);
      }

      refresh();
    } catch (error) {
      console.error('Bulk reject error:', error);
      toast.dismiss(toastId);
      toast.error('An error occurred during bulk rejection');
    }
  };

  const handleExport = async (selectedData: Expense[], includeFields: string[]) => {
    try {
      // Helper function to format date as dd-mmm-yyyy
      const formatDateForExcel = (dateValue: any): string => {
        if (!dateValue) return '';
        
        const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
        if (isNaN(date.getTime())) return String(dateValue);
        
        const day = String(date.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        
        return `${day}-${month}-${year}`;
      };

      // Get current date range for cost calculation (last 30 days)
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      // Get unique unit IDs from selected data
      const unitIds = [...new Set(selectedData.map(e => e.unitId).filter(Boolean))];

      if (unitIds.length === 0) {
        toast.error("No units found in selected data");
        return;
      }

      // Calculate cost per bag for all units
      const costData = await CostRollupService.calculateMultipleUnits(
        unitIds,
        startDate,
        endDate,
        true, // includeAllSagoTypes
        true  // approvedOnly
      );

      // Prepare export data
      const exportData = selectedData.map(expense => {
        const row: any = {};
        includeFields.forEach(field => {
          switch (field) {
            case 'date':
              row.Date = formatDateForExcel(expense.date);
              break;
            case 'type':
              row.Type = expense.type;
              break;
            case 'unit':
              row.Unit = expense.unit;
              break;
            case 'costCenter':
              row['Cost Center'] = expense.costCenter;
              break;
            case 'category':
              row.Category = expense.category;
              break;
            case 'nature':
              row.Nature = expense.nature || 'N/A';
              break;
            case 'shop':
              row.Shop = getShopName(expense.shop);
              break;
            case 'amount':
              row.Amount = expense.amount;
              break;
            case 'description':
              row.Description = expense.description;
              break;
            case 'name':
              row.Name = expense.name;
              break;
            case 'approvalStatus':
              row.Status = expense.approvalStatus;
              break;
            case 'createdBy':
              row['Created By'] = expense.createdBy;
              break;
            case 'createdAt':
              row['Created Date'] = formatDateForExcel(expense.createdAt);
              break;
            case 'updatedBy':
              row['Updated By'] = expense.updatedBy || 'N/A';
              break;
            case 'updatedAt':
              row['Updated Date'] = expense.updatedAt ? formatDateForExcel(expense.updatedAt) : 'Never';
              break;
          }
        });
        return row;
      });

      // Add cost per bag summary if requested
      const costSummary = costData.map(cost => {
        const row: any = {};
        includeFields.forEach(field => {
          switch (field) {
            case 'date':
              row.Date = `${startDate} to ${endDate}`;
              break;
            case 'type':
              row.Type = 'COST_PER_BAG_KPI';
              break;
            case 'unit':
              row.Unit = cost.unitName;
              break;
            case 'costCenter':
              row['Cost Center'] = 'PRODUCTION';
              break;
            case 'category':
              row.Category = 'MANUFACTURING';
              break;
            case 'nature':
              row.Nature = 'KPI';
              break;
            case 'shop':
              row.Shop = 'ALL';
              break;
            case 'amount':
              row.Amount = cost.costPerBag;
              break;
            case 'description':
              row.Description = `Cost per bag: ₹${cost.costPerBag.toFixed(2)} (${cost.totalProductionBags} bags, ₹${cost.totalApprovedExpenses.toLocaleString()} expenses)`;
              break;
            case 'name':
              row.Name = 'SYSTEM_CALCULATED';
              break;
            case 'approvalStatus':
              row.Status = 'CALCULATED';
              break;
            case 'createdBy':
              row['Created By'] = 'SYSTEM';
              break;
            case 'createdAt':
              row['Created Date'] = new Date().toISOString();
              break;
            case 'updatedBy':
              row['Updated By'] = 'N/A';
              break;
            case 'updatedAt':
              row['Updated Date'] = 'Never';
              break;
          }
        });
        return row;
      });

      const fullExportData = [...exportData, ...costSummary];

      // Convert to CSV
      const headers = Object.keys(fullExportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...fullExportData.map(row =>
          headers.map(header => {
            const value = row[header];
            const stringValue = String(value || '');
            return stringValue.includes(',') || stringValue.includes('"')
              ? `"${stringValue.replace(/"/g, '""')}"`
              : stringValue;
          }).join(',')
        )
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `expense-management-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${fullExportData.length} records including cost per bag KPIs`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data with cost KPIs');
    }
  };

  // Helper functions for display amounts
  const getDisplayAmount = (expense: Expense): string => {
    if (typeof expense.amount === 'number') {
      return `₹${expense.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (expense.amount && typeof expense.amount === 'string') {
      const numAmount = parseFloat(expense.amount);
      return isNaN(numAmount) ? '₹0.00' : `₹${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return '₹0.00';
  };

  const getDisplayTaxAmount = (expense: Expense): string => {
    if (typeof expense.taxAmount === 'number') {
      return `₹${expense.taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (expense.taxAmount && typeof expense.taxAmount === 'string') {
      const numTaxAmount = parseFloat(expense.taxAmount);
      return isNaN(numTaxAmount) ? '₹0.00' : `₹${numTaxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return '₹0.00';
  };

  return (
    <div className="space-y-4">
      <SectionCard title="">
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading expense data...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-8">
            <div className="text-ds-status-error-main mb-4">Error loading data: {error}</div>
            <button
              onClick={() => refresh()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <div className="w-full">
            {/* Status Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-info-100 mb-4">
              <div className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-text-primary">Expense Records</h2>
                    <p className="text-sm text-text-secondary mt-1">Manage and review expense entries</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-ds-primary-800">
                      <span className="font-medium">Records:</span> {expenses.length} shown
                      {hasNextPage && (
                        <>
                          <span className="text-ds-primary-600"> (More available)</span>
                          <Button
                            variant="primary"
                            onClick={loadNextPage}
                            className="ml-2 text-xs"
                          >
                            Load More
                          </Button>
                        </>
                      )}
                      {fullFilterData.length > 0 && (
                        <span className="text-text-secondary"> • Full dataset: {fullFilterData.length} records</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Table */}
            <div className="w-full">
              <EnterpriseDataTable
                data={expenses.map(e => ({
                  ...e,
                  shop: getShopName(e.shop), // Transform shop ID to name
                  displayAmount: getDisplayAmount(e),
                  displayTaxAmount: getDisplayTaxAmount(e),
                }))}
                fullData={filterOptionsData}
                masterData={{
                  types,
                  units,
                  costCenters,
                  categories,
                  shops,
                  vendors
                }}
                loading={loading}
                isAdmin={isAdmin}
                userRoles={roles}
                assignedUnits={assignedUnits}
                canEdit={canUserEdit}
                canDelete={(expense: Expense) => roles.includes('admin') || roles.includes('superadmin')}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onApprove={handleApprove}
                onReject={handleReject}
                onBulkApprove={handleBulkApprove}
                onBulkReject={handleBulkReject}
                rowLoading={rowLoading}
                onExport={handleExport}
                tableClassName="w-full table-auto"
                containerClassName="overflow-x-auto"
                stickyPagination={true}
                columnWidths={{
                  date: '120px',
                  type: '100px',
                  unit: '100px',
                  costCenter: '140px',
                  category: '140px',
                  nature: '110px',
                  shop: '150px',
                  amount: '120px',
                  expand: '40px',
                  approvalStatus: '110px',
                  createdBy: '150px',
                  createdAt: '140px',
                  actions: '130px',
                }}
                columnClassNames={{
                  date: 'text-left',
                  type: 'text-left',
                  unit: 'text-left',
                  costCenter: 'text-left',
                  category: 'text-left',
                  nature: 'text-center',
                  shop: 'text-left',
                  amount: 'text-right font-medium',
                  approvalStatus: 'text-center',
                  createdBy: 'text-left',
                  updatedAt: 'text-left',
                  actions: 'text-center',
                }}
              />
            </div>

            {expenses.length === 0 && (
              <div className="text-center py-12">
                <Database className="h-16 w-16 text-text-disabled mx-auto mb-4" />
                <p className="text-lg text-text-secondary font-medium">No expenses found</p>
                <p className="text-sm text-text-muted mt-2">Try adjusting your filters or add a new expense</p>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Edit Modal */}
      {editExpense && (
        <EditEntryModal
          open={!!editExpense}
          entry={editExpense}
          onClose={handleEditClose}
          onSaved={handleEditSaved}
          types={types}
          units={units}
          costCenters={costCenters}
          categories={categories.map(c => ({
            ...c,
            type: c.type || '',
            costCenter: c.costCenter || '',
            needsNameField: c.requiresNameField || false
          }))}
          userEmail={user?.email || ""}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog {...dialogProps} />
    </div>
  );
};

export default ExpenseManagementPage;
